import { useState, useEffect, useContext, createContext, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';

/**
 * Module Access Control Hook and Context
 *
 * SOC 2 Controls:
 * - CC6.1: Feature-level access security (client-side enforcement)
 * - CC6.3: Tenant feature segregation
 *
 * IMPORTANT: This is UI-level access control only.
 * Backend MUST still enforce module access via decorators.
 * Never rely on frontend-only security.
 *
 * Usage:
 *   const { isModuleEnabled, enabledModules, loading } = useModuleAccess();
 *
 *   if (isModuleEnabled('hira')) {
 *     // Show HIRA features
 *   }
 */

// Create Module Access Context
const ModuleAccessContext = createContext({
  enabledModules: [],
  enabledModuleSlugs: [],
  loading: true,
  isModuleEnabled: () => false,
  hasAnyModule: () => false,
  hasAllModules: () => false,
  refreshModules: () => {}
});

/**
 * Parent-to-children module mapping.
 * Parent modules (tabs) map to their child sub-module slugs.
 * Used to check if a user has permission for ANY child module of a parent.
 */
const MODULE_CHILDREN = {
  'safety': ['safety', 'hira', 'ptw', 'capa', 'incidents', 'observations', 'training', 'audits', 'meetings', 'ssem', 'documents'],
  'energy': ['ers', 'cbam', 'energy', 'emissions'],
  'sustainability': ['ssum', 'cbam', 'gri', 'lca', 'brsr', 'sgx', 'ecovadis'],
  'board': ['erm', 'board_process'],
};

/**
 * Module Access Provider Component
 *
 * Wrap your app with this provider to enable module access checks.
 * Combines tenant module enablement with user RBAC permissions.
 *
 * SOC 2 CC6.1, CC6.2: Module access gated by both tenant subscription
 * and user role permissions.
 *
 * Example:
 *   <ModuleAccessProvider>
 *     <App />
 *   </ModuleAccessProvider>
 */
export const ModuleAccessProvider = ({ children }) => {
  const [enabledModules, setEnabledModules] = useState([]);
  const [enabledModuleSlugs, setEnabledModuleSlugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const { hasModulePermission, isAdmin, loading: permLoading } = usePermissions();

  /**
   * Fetch enabled modules from API
   * Automatically re-fetches when token changes (on login/logout)
   */
  const fetchEnabledModules = useCallback(async () => {
    if (!token) {
      setEnabledModules([]);
      setEnabledModuleSlugs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Try PostgreSQL endpoint first, fall back to MongoDB endpoint
      let response;
      try {
        response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/tenant-admin/modules`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (pgError) {
        // Fallback to legacy MongoDB endpoint
        console.log('Falling back to legacy modules endpoint');
        response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/tenant/modules`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      const modules = response.data.enabled_modules || [];
      setEnabledModules(modules);
      setEnabledModuleSlugs(modules.map(m => m.slug));
    } catch (error) {
      console.error('Error fetching enabled modules:', error);
      // Don't throw error - fail open for UI (backend will enforce)
      setEnabledModules([]);
      setEnabledModuleSlugs([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch modules when token changes (login/logout)
  useEffect(() => {
    fetchEnabledModules();
  }, [fetchEnabledModules]);

  /**
   * Check if a specific module is enabled for the current tenant
   * AND the user has permission to access it.
   *
   * SOC 2 CC6.1, CC6.2: Enforces both tenant subscription and user RBAC.
   *
   * @param {string} moduleSlug - Module slug to check (e.g., 'hira', 'ptw')
   * @param {string|null} siteId - Optional site ID for site-level module config (reserved for Phase 1 backend)
   * @returns {boolean} - True if module is enabled AND user has access
   */
  const isModuleEnabled = (moduleSlug, siteId = null) => {
    // Step 1: Check if module is enabled for the tenant (subscription level)
    const tenantEnabled = enabledModuleSlugs.includes(moduleSlug);
    if (!tenantEnabled) return false;

    // Step 2: Admin users bypass RBAC permission check
    if (isAdmin()) return true;

    // Step 3: If permissions are still loading, deny access (fail closed).
    // Backend enforces regardless; UI should not flash unauthorized modules.
    // SOC 2 CC6.7: Fail closed on ambiguous authorization state.
    if (permLoading) return false;

    // Step 4: For parent/hub modules, check if user has permission for ANY child
    const childModules = MODULE_CHILDREN[moduleSlug];
    if (childModules) {
      return childModules.some(child => hasModulePermission(child));
    }

    // Step 5: For leaf modules, check direct module permission
    return hasModulePermission(moduleSlug);
  };

  /**
   * Check if ANY of the specified modules is enabled and accessible
   *
   * @param {...string} moduleSlugs - Variable number of module slugs
   * @returns {boolean} - True if at least one module is enabled and accessible
   */
  const hasAnyModule = (...moduleSlugs) => {
    return moduleSlugs.some(slug => isModuleEnabled(slug));
  };

  /**
   * Check if ALL specified modules are enabled and accessible
   *
   * @param {...string} moduleSlugs - Variable number of module slugs
   * @returns {boolean} - True if all modules are enabled and accessible
   */
  const hasAllModules = (...moduleSlugs) => {
    return moduleSlugs.every(slug => isModuleEnabled(slug));
  };

  /**
   * Refresh enabled modules (call after subscription changes)
   */
  const refreshModules = () => {
    setLoading(true);
    fetchEnabledModules();
  };

  const value = {
    enabledModules,
    enabledModuleSlugs,
    loading,
    isModuleEnabled,
    hasAnyModule,
    hasAllModules,
    refreshModules
  };

  return (
    <ModuleAccessContext.Provider value={value}>
      {children}
    </ModuleAccessContext.Provider>
  );
};

/**
 * Custom hook to access module availability
 *
 * @returns {Object} Module access context
 */
export const useModuleAccess = () => {
  const context = useContext(ModuleAccessContext);
  if (!context) {
    throw new Error('useModuleAccess must be used within ModuleAccessProvider');
  }
  return context;
};

/**
 * HOC to require a module for a component
 *
 * If module is not enabled, shows upgrade message
 *
 * Example:
 *   export default withModuleAccess('hira')(HIRAComponent);
 */
export const withModuleAccess = (moduleSlug, fallbackComponent = null) => (Component) => {
  return (props) => {
    const { isModuleEnabled, loading } = useModuleAccess();

    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    if (!isModuleEnabled(moduleSlug)) {
      if (fallbackComponent) {
        return fallbackComponent;
      }

      return (
        <ModuleNotAvailable moduleSlug={moduleSlug} />
      );
    }

    return <Component {...props} />;
  };
};

/**
 * Default "Module Not Available" component
 *
 * Shown when user tries to access a disabled module
 */
const ModuleNotAvailable = ({ moduleSlug }) => {
  const moduleNames = {
    'hira': 'HIRA (Risk Assessment)',
    'ptw': 'Permit To Work',
    'capa': 'CAPA (Corrective Actions)',
    'incidents': 'Incident Management',
    'observations': 'Observations',
    'audits': 'Audits & Inspections',
    'ssem': 'SSEM',
    'training': 'Training Management',
    'meetings': 'Meeting Management',
    'documents': 'Document Control',
    'ers': 'Energy & Resources',
    'cbam': 'CBAM',
    'ssum': 'SSUM',
    'gri': 'GRI',
    'lca': 'LCA',
    'brsr': 'BRSR',
    'sgx': 'SGX',
    'ecovadis': 'EcoVadis',
    'erm': 'ERM',
    'board_process': 'Board Process'
  };

  const moduleName = moduleNames[moduleSlug] || moduleSlug.toUpperCase();

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Module Not Available</h2>
        <p className="text-gray-600 mb-6">
          The <strong>{moduleName}</strong> module is not enabled for your organization.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-900">
            To access this feature, please contact your administrator to enable this module or upgrade your subscription plan.
          </p>
        </div>
        <button
          onClick={() => window.history.back()}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

/**
 * Component to conditionally render children based on module availability
 *
 * Example:
 *   <ModuleGuard module="hira">
 *     <HIRAFeature />
 *   </ModuleGuard>
 *
 *   <ModuleGuard module="hira" fallback={<UpgradePrompt />}>
 *     <HIRAFeature />
 *   </ModuleGuard>
 */
export const ModuleGuard = ({ module, children, fallback = null, showUpgrade = false }) => {
  const { isModuleEnabled, loading } = useModuleAccess();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading module access...</p>
        </div>
      </div>
    );
  }

  if (!isModuleEnabled(module)) {
    if (fallback) {
      return fallback;
    }

    if (showUpgrade) {
      return <ModuleNotAvailable moduleSlug={module} />;
    }

    return null;
  }

  return children;
};

/**
 * Component to show content only if ANY of the specified modules is enabled
 *
 * Example:
 *   <ModuleGuardAny modules={['documents', 'training', 'audits']}>
 *     <SharedDocumentLibrary />
 *   </ModuleGuardAny>
 */
export const ModuleGuardAny = ({ modules, children, fallback = null, showUpgrade = false }) => {
  const { hasAnyModule, loading } = useModuleAccess();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading module access...</p>
        </div>
      </div>
    );
  }

  if (!hasAnyModule(...modules)) {
    if (fallback) {
      return fallback;
    }

    if (showUpgrade) {
      // Show the first module in the list as the required module
      return <ModuleNotAvailable moduleSlug={modules[0]} />;
    }

    return null;
  }

  return children;
};

/**
 * Component to show content only if ALL specified modules are enabled
 *
 * Example:
 *   <ModuleGuardAll modules={['hira', 'incidents']}>
 *     <AdvancedAnalytics />
 *   </ModuleGuardAll>
 */
export const ModuleGuardAll = ({ modules, children, fallback = null, showUpgrade = false }) => {
  const { hasAllModules, loading } = useModuleAccess();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading module access...</p>
        </div>
      </div>
    );
  }

  if (!hasAllModules(...modules)) {
    if (fallback) {
      return fallback;
    }

    if (showUpgrade) {
      // Show the first module in the list as the required module
      return <ModuleNotAvailable moduleSlug={modules[0]} />;
    }

    return null;
  }

  return children;
};

export default useModuleAccess;
